import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // 从 localStorage 加载主题设置
  useEffect(() => {
    // 防止初始加载时的过渡动画
    document.body.classList.add('no-transition');
    
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    } else {
      setIsDarkMode(prefersDark);
    }
    
    // 移除no-transition类，允许后续的过渡动画
    setTimeout(() => {
      document.body.classList.remove('no-transition');
    }, 100);
  }, []);

  // 更新body的data-theme属性和meta theme-color
  useEffect(() => {
    document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    
    // 更新meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDarkMode ? '#1a1a1a' : '#ffffff');
    }
  }, [isDarkMode]);

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // 只有在没有手动设置主题时才跟随系统
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const theme = {
    isDarkMode,
    toggleTheme,
    colors: {
      // 背景色
      background: isDarkMode ? '#1a1a1a' : '#ffffff',

      // 卡片背景
      cardBackground: isDarkMode
        ? 'rgba(60, 60, 60, 0.95)'
        : 'rgba(255, 255, 255, 0.95)',

      // 文字颜色
      text: isDarkMode ? '#ffffff' : '#333333',
      textSecondary: isDarkMode ? '#b0b0b0' : '#666666',
      textInverse: isDarkMode ? '#ffffff' : '#ffffff',

      // 边框颜色
      border: isDarkMode ? '#404040' : '#e0e0e0',
      borderHover: isDarkMode ? '#606060' : '#d0d0d0',

      // 输入框
      inputBackground: isDarkMode ? '#2a2a2a' : '#f5f5f5',
      inputBorder: isDarkMode ? '#404040' : '#e0e0e0',
      inputFocus: '#4a90e2',

      // 按钮
      primary: '#4a90e2',
      primaryHover: '#357abd',

      // 头部导航
      headerBackground: isDarkMode
        ? 'rgba(45, 45, 45, 0.95)'
        : 'rgba(255, 255, 255, 0.95)',
      headerText: isDarkMode ? '#ffffff' : '#333333',

      // 链接
      link: isDarkMode ? '#5aa0ff' : '#4a90e2',
      linkHover: isDarkMode ? '#7ab8ff' : '#357abd',

      // 错误和成功色
      error: isDarkMode ? '#ff6b6b' : '#ff3b30',
      success: isDarkMode ? '#51cf66' : '#34c759',
      warning: isDarkMode ? '#ffd43b' : '#ff9500'
    }
  };

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}; 